import { log } from '../app/logger.js';
import { navigation } from '../app/navigation.js';
import { UIQueueable } from '../ui/ui_queueable.js';
import { screenMedia } from '../util/util.js';
import { window } from '../window.js';
import { PicUploadPopupMixin } from './picupload_popup_mixin.js';

import '../media/picupload_control.js';
import './picupload_panel.js';

// A helper class representing a picupload control in a map popup or in a panel.
class PicUploadViewer {
  constructor({ map, markers, pics }) {
    this.#map = map;
    this.#markers = markers;

    this.#picUploadControl = window.document.createElement('picupload-control');
    this.#picUploadControl.map = map;
    this.setPics(pics);

    screenMedia.registerUI(isMobile => {
      isMobile
        ? this.#picUploadControl.onmaximize?.()
        : this.#picUploadControl.onminimize?.();
    });
  }

  show(marker) {
    const isMobile = screenMedia.isMobile();
    log(
      `[picupload] show picupload ${isMobile ? 'panel' : 'popup'} for '${
        marker.title
      }'`
    );

    this.#setUI(isMobile);
    return isMobile
      ? navigation.openPicUpload({ markerid: marker.id })
      : this.picUploadPopup.showFor(marker);
  }

  setPics(pics) {
    log(`[picupload] set pics, count: ${pics.length}`);
    this.#picUploadControl.setPics(pics);
  }

  get picUploadPopup() {
    if (!this.#picUploadPopup) {
      this.#picUploadPopup = new (PicUploadPopupMixin(window.MapPopup))();
    }
    return this.#picUploadPopup;
  }

  #setUI(isMaximized) {
    // PicUpload panel.
    if (isMaximized) {
      log(`[picupload] attached to picupload panel`);
      navigation.picUpload.attach({
        map: this.#map,
        markers: this.#markers,
        picUploadControl: this.#picUploadControl,
      });

      this.#picUploadControl.onpicchange = null;
      this.#picUploadControl.onclose = UIQueueable(() =>
        navigation.closePicUpload()
      );
      this.#picUploadControl.onminimize = UIQueueable(() => {
        navigation.closePicUpload();
        this.#setUI(false);
        this.picUploadPopup.showFor(navigation.picUpload.marker, 'doNotSetPic');
      });
      this.#picUploadControl.onmaximize = null;
      this.#picUploadControl.sizezable = !screenMedia.isMobile();
      this.#picUploadControl.maximized = true;
      return;
    }

    // PicUpload popup.
    log(`[picupload] attached to picupload popup`);
    this.picUploadPopup.attachPicUploadControl(this.#picUploadControl);

    this.#picUploadControl.onclose = () => this.picUploadPopup.deattach();
    this.#picUploadControl.onpicchange = ({ marker }) => {
      if (this.picUploadPopup.marker != marker) {
        this.picUploadPopup.showFor(marker, 'doNotSetPic');
      }
    }
    this.#picUploadControl.onminimize = null;
    this.#picUploadControl.onmaximize = UIQueueable(() => {
      this.#setUI(true);
      this.picUploadPopup.close();
      navigation.openPicUpload({
        markerid: this.picUploadPopup.marker.id,
        doNotSetPic: true,
      });
    });
    this.#picUploadControl.sizezable = true;
    this.#picUploadControl.maximized = false;
  }

  #markers;
  #map;
  #picUploadControl;
  #picUploadPopup;
}

export { PicUploadViewer };
